import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useOrgStore } from './orgStore.js'
import {
  createSubOrgRequest,
  restoreSubOrgRequest,
  softDeleteSubOrg,
  updateSubOrgRequest,
} from '../services/organizationService.js'

export const useSubOrgStore = defineStore('subOrg', () => {
  const orgStore = useOrgStore()
  const subOrgs = computed(() => orgStore.subOrgs)
  const isLoaded = ref(false)
  const loading = ref(false)
  const error = ref('')
  const currentSubOrgId = computed({
    get: () => orgStore.activeSubOrgId,
    set: (value) => {
      setCurrentSubOrg(value)
    },
  })

  const byOrg = computed(() => {
    return (orgId) => {
      if (!orgId) return []
      return subOrgs.value.filter((item) => Number(item?.organization_id) === Number(orgId))
    }
  })

  const getSubOrgName = (subOrgId) => {
    return subOrgs.value.find((item) => Number(item?.id) === Number(subOrgId))?.name ?? `SubOrg #${subOrgId}`
  }

  const setCurrentSubOrg = (subOrgId) => {
    orgStore.setActiveSubOrg(subOrgId)
  }

  const resetSubOrgContext = () => {
    isLoaded.value = false
    loading.value = false
    error.value = ''
    setCurrentSubOrg(null)
  }

  const fetchSubOrgs = async (options = {}) => {
    if (!orgStore.activeOrgId) {
      isLoaded.value = false
      loading.value = false
      error.value = ''
      setCurrentSubOrg(null)
      return { success: true, data: [] }
    }

    loading.value = true
    error.value = ''

    try {
      const result = await orgStore.fetchSubOrgs({ status: options?.status ?? 'active' })
      if (!result?.success) {
        isLoaded.value = false
        error.value = result?.error ?? 'Failed to fetch sub-organizations'
        return { success: false, error: error.value }
      }
      isLoaded.value = true
      return { success: true, data: subOrgs.value }
    } catch (err) {
      isLoaded.value = false
      setCurrentSubOrg(null)
      error.value = err?.response?.data?.detail ?? 'Failed to fetch sub-organizations'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const createSubOrg = async (name, description = '', organizationId) => {
    loading.value = true
    error.value = ''
    try {
      const normalizedOrgId = Number(organizationId)
      if (!Number.isInteger(normalizedOrgId) || normalizedOrgId <= 0) {
        error.value = 'Organization must be selected'
        return { success: false, error: error.value }
      }

      const created = await createSubOrgRequest(name, description, normalizedOrgId)
      await fetchSubOrgs()
      return { success: true, data: created }
    } catch (err) {
      error.value = err?.response?.data?.detail ?? 'Failed to create sub-organization'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updateSubOrg = async (subOrgId, name, description = '') => {
    loading.value = true
    error.value = ''
    try {
      const updated = await updateSubOrgRequest(subOrgId, name, description)
      await fetchSubOrgs()
      return { success: true, data: updated }
    } catch (err) {
      error.value = err?.response?.data?.detail ?? 'Failed to update sub-organization'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const deleteSubOrg = async (subOrgId) => {
    loading.value = true
    error.value = ''
    try {
      await softDeleteSubOrg(subOrgId)
      await fetchSubOrgs()
      return { success: true }
    } catch (err) {
      error.value = err?.response?.data?.detail ?? 'Failed to delete sub-organization'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const restoreSubOrg = async (subOrgId) => {
    loading.value = true
    error.value = ''
    try {
      const restored = await restoreSubOrgRequest(subOrgId)
      await fetchSubOrgs({ status: 'all' })
      return { success: true, data: restored }
    } catch (err) {
      error.value = err?.response?.data?.detail ?? 'Failed to restore sub-organization'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  watch(
    () => orgStore.activeOrgId,
    async (nextOrgId, previousOrgId) => {
      if (nextOrgId === previousOrgId) return
      setCurrentSubOrg(null)

      if (!nextOrgId) {
        isLoaded.value = false
        error.value = ''
        return
      }

      await fetchSubOrgs()
    },
  )

  return {
    subOrgs,
    isLoaded,
    loading,
    error,
    currentSubOrgId,
    byOrg,
    getSubOrgName,
    setCurrentSubOrg,
    resetSubOrgContext,
    fetchSubOrgs,
    createSubOrg,
    updateSubOrg,
    deleteSubOrg,
    restoreSubOrg,
  }
})
